import React, { useEffect, useState } from 'react'
import { apiGet, apiPost } from '../utils/api'

// Функция для форматирования чисел с разделителями тысяч в русской локали (округление до сотых)
const formatNumber = (num: number): string => {
  return num.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// Функция для форматирования целых чисел с разделителями тысяч
const formatInteger = (num: number): string => {
  return Math.round(num).toLocaleString('ru-RU')
}

interface InventoryBalanceItem {
  id: string
  name: string
  unit: string
  quantity: number
  averageCost?: number
  totalCost?: number
}

const ReportsInventoryBalances: React.FC = () => {
  const [items, setItems] = useState<InventoryBalanceItem[]>([])
  const [warehouses, setWarehouses] = useState<Array<{id:string; name:string}>>([])
  const [warehouseId, setWarehouseId] = useState<string>('')
  const [mode, setMode] = useState<'flat' | 'item-warehouse' | 'warehouse-item'>('flat')
  const [grouped, setGrouped] = useState<any>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [isInitialized, setIsInitialized] = useState(false)
  const [lastValidFrom, setLastValidFrom] = useState('')
  const [lastValidTo, setLastValidTo] = useState('')
  
  // Загружаем сохраненный период при инициализации
  useEffect(() => {
    const savedFrom = localStorage.getItem('inventory-balances-report-from')
    const savedTo = localStorage.getItem('inventory-balances-report-to')
    if (savedFrom) setDateFrom(savedFrom)
    if (savedTo) setDateTo(savedTo)
    setIsInitialized(true)
  }, [])
  const [docNumber, setDocNumber] = useState<string>('')
  const [allBaseItems, setAllBaseItems] = useState<{ id: string; name: string }[]>([])
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([])
  const [isRecalcOpen, setIsRecalcOpen] = useState<boolean>(false)

  // Сохраняем период в localStorage
  const savePeriod = (fromDate: string, toDate: string) => {
    if (fromDate) {
      localStorage.setItem('inventory-balances-report-from', fromDate)
    } else {
      localStorage.removeItem('inventory-balances-report-from')
    }
    if (toDate) {
      localStorage.setItem('inventory-balances-report-to', toDate)
    } else {
      localStorage.removeItem('inventory-balances-report-to')
    }
  }

  // Сброс периода
  const resetPeriod = () => {
    setDateFrom('')
    setDateTo('')
    setLastValidFrom('')
    setLastValidTo('')
    localStorage.removeItem('inventory-balances-report-from')
    localStorage.removeItem('inventory-balances-report-to')
  }

  const fetchBalances = async () => {
    setLoading(true)
    setError('')
    try {
      // Склады
      const wJson = await apiGet<Array<{id:string; name:string}>>('/warehouses')
      setWarehouses(Array.isArray(wJson) ? wJson : [])

      // 1) Получаем перечень ТМЦ
      const listData = await apiGet<{ items?: any[] }>('/inventory-items?limit=1000')
      const baseItems: any[] = Array.isArray(listData.items) ? listData.items : []
      setAllBaseItems(baseItems.map((it: any) => ({ id: it.id, name: it.name })))

      // Если выбран групповой режим — используем новый отчет и заканчиваем
      if (mode === 'item-warehouse' || mode === 'warehouse-item') {
        const data = await apiGet<any>(`/reports/inventory-balances?group=${mode}`)
        
        // Фильтруем позиции с нулевым остатком для групповых режимов
        if (data.items) {
          data.items = data.items.filter((item: any) => item.totalQuantity !== 0)
        }
        if (data.warehouses) {
          data.warehouses = data.warehouses.map((warehouse: any) => ({
            ...warehouse,
            items: warehouse.items.filter((item: any) => item.quantity !== 0)
          })).filter((warehouse: any) => warehouse.items.length > 0)
        }
        
        setGrouped(data)
        setItems([])
        setLoading(false)
        return
      }

      // 2) Для каждого ТМЦ тянем баланс (плоский режим)
      const withBalances = await Promise.all(baseItems.map(async (it) => {
        try {
          const params: Record<string, string> = {
            mode: 'live'
          }
          if (warehouseId) params.warehouseId = warehouseId
          if (dateFrom) params.from = dateFrom
          if (dateTo) params.to = dateTo
          const bJson = await apiGet<{ balance?: number; amount?: number }>(`/inventory-items/${it.id}/balance`, params)
          const quantity = Number(bJson.balance) || 0
          const totalCost = Number(bJson.amount) || 0
          const averageCost = quantity > 0 ? (totalCost / quantity) : undefined
          return {
            id: it.id,
            name: it.name,
            unit: it.unit || 'шт',
            quantity,
            averageCost,
            totalCost
          } as InventoryBalanceItem
        } catch {
          return {
            id: it.id,
            name: it.name,
            unit: it.unit || 'шт',
            quantity: 0
          } as InventoryBalanceItem
        }
      }))

      // Фильтруем позиции с нулевым остатком
      const filteredItems = withBalances.filter(item => item.quantity !== 0)
      setItems(filteredItems)
    } catch (e: any) {
      setError(e.message || 'Ошибка')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isInitialized) {
      // Проверяем, что дата действительно изменилась и является валидной
      const fromChanged = dateFrom !== lastValidFrom && dateFrom !== ''
      const toChanged = dateTo !== lastValidTo && dateTo !== ''
      
      // Также загружаем если обе даты пустые (сброс)
      const bothEmpty = dateFrom === '' && dateTo === '' && (lastValidFrom !== '' || lastValidTo !== '')
      
      // Загружаем при инициализации если нет сохраненных дат
      const initialLoad = dateFrom === '' && dateTo === '' && lastValidFrom === '' && lastValidTo === ''
      
      if (fromChanged || toChanged || bothEmpty || initialLoad || warehouseId !== '' || mode !== 'flat') {
        // Небольшая задержка для предотвращения множественных вызовов
        const timer = setTimeout(() => {
          fetchBalances()
          setLastValidFrom(dateFrom)
          setLastValidTo(dateTo)
        }, 300)
        
        return () => clearTimeout(timer)
      }
    }
  }, [isInitialized, warehouseId, mode, dateFrom, dateTo, lastValidFrom, lastValidTo])

  const handleToggleAll = () => {
    if (selectedItemIds.length === allBaseItems.length) {
      setSelectedItemIds([])
    } else {
      setSelectedItemIds(allBaseItems.map(i => i.id))
    }
  }

  const handleRecalc = async () => {
    setLoading(true)
    setError('')
    try {
      const body: any = {
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        itemIds: selectedItemIds.length > 0 ? selectedItemIds : undefined,
        cleanupDocNumber: docNumber || undefined
      }
      const data = await apiPost<any>('/inventory/recalc', body)
      const recalcItems: InventoryBalanceItem[] = Array.isArray(data.items) ? data.items.map((it: any) => ({
        id: it.id,
        name: allBaseItems.find(b => b.id === it.id)?.name || it.name || it.id,
        unit: 'шт',
        quantity: Number(it.quantity) || 0,
        averageCost: it.averageCost != null ? Number(it.averageCost) : undefined,
        totalCost: it.amount != null ? Number(it.amount) : undefined
      })) : []
      setItems(recalcItems)
    } catch (e: any) {
      setError(e.message || 'Ошибка')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Остатки ТМЦ</h2>
        <div className="flex items-center gap-2">
          <select
            value={mode}
            onChange={(e)=> setMode(e.target.value as any)}
            className="px-3 py-2 text-sm border rounded-md"
          >
            <option value="flat">Плоский список</option>
            <option value="item-warehouse">Номенклатура → Склады</option>
            <option value="warehouse-item">Склады → Номенклатура</option>
          </select>
          <select
            value={warehouseId}
            onChange={(e)=> setWarehouseId(e.target.value)}
            className="px-3 py-2 text-sm border rounded-md"
          >
            <option value="">Все склады</option>
            {warehouses.map(w => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 text-sm border rounded-md"
            placeholder="От даты"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 text-sm border rounded-md"
            placeholder="До даты"
          />
          <button
            onClick={() => { savePeriod(dateFrom, dateTo); fetchBalances(); }}
            className="px-3 py-2 text-sm bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 border"
          >
            Обновить
          </button>
          <button
            onClick={resetPeriod}
            className="px-3 py-2 text-sm bg-red-100 text-red-800 rounded-md hover:bg-red-200 border"
          >
            Сбросить
          </button>
        </div>
      </div>


      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md border border-red-200">{error}</div>
      )}
      {loading ? (
        <p className="text-gray-600">Загрузка...</p>
      ) : mode !== 'flat' ? (
        // Групповые режимы — аккордеоны
        mode === 'item-warehouse' && grouped?.items ? (
          <div className="space-y-2">
            {grouped.items.map((it: any) => (
              <details key={it.id} className="bg-white rounded border">
                <summary className="cursor-pointer select-none px-4 py-2 font-medium flex items-center justify-between">
                  <span>{it.name} <span className="text-gray-500 text-sm">({it.unit})</span></span>
                  <span className="text-gray-700 text-sm">Всего: {formatInteger(Number(it.totalQuantity || 0))} | Сумма: {formatNumber(Number(it.totalCost || 0))}</span>
                </summary>
                <div className="px-4 pb-3">
                  <table className="min-w-full">
                    <thead>
                      <tr className="text-xs text-gray-500">
                        <th className="text-left py-1">Склад</th>
                        <th className="text-right py-1">Кол-во</th>
                        <th className="text-right py-1">Средняя с/с</th>
                        <th className="text-right py-1">Сумма</th>
                      </tr>
                    </thead>
                    <tbody>
                      {it.warehouses.map((w: any) => (
                        <tr key={w.id} className="text-sm">
                          <td className="py-1">{w.name}</td>
                          <td className="py-1 text-right">{formatInteger(Number(w.quantity))}</td>
                          <td className="py-1 text-right">{w.averageCost != null ? formatNumber(Number(w.averageCost)) : '-'}</td>
                          <td className="py-1 text-right">{w.totalCost != null ? formatNumber(Number(w.totalCost)) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </div>
        ) : mode === 'warehouse-item' && grouped?.warehouses ? (
          <div className="space-y-2">
            {grouped.warehouses.map((w: any) => (
              <details key={w.warehouseId} className="bg-white rounded border">
                <summary className="cursor-pointer select-none px-4 py-2 font-medium">
                  {w.warehouseName}
                </summary>
                <div className="px-4 pb-3">
                  <table className="min-w-full">
                    <thead>
                      <tr className="text-xs text-gray-500">
                        <th className="text-left py-1">ТМЦ</th>
                        <th className="text-right py-1">Кол-во</th>
                        <th className="text-right py-1">Средняя с/с</th>
                        <th className="text-right py-1">Сумма</th>
                      </tr>
                    </thead>
                    <tbody>
                      {w.items.map((it: any) => (
                        <tr key={it.id} className="text-sm">
                          <td className="py-1">{it.name}</td>
                          <td className="py-1 text-right">{formatInteger(Number(it.quantity))}</td>
                          <td className="py-1 text-right">{it.averageCost != null ? formatNumber(Number(it.averageCost)) : '-'}</td>
                          <td className="py-1 text-right">{it.totalCost != null ? formatNumber(Number(it.totalCost)) : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </div>
        ) : (
          <p className="text-gray-600">Нет данных.</p>
        )
      ) : items.length === 0 ? (
        <p className="text-gray-600">Нет данных по остаткам.</p>
      ) : (
        <div className="overflow-x-auto bg-white shadow rounded-lg border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ТМЦ</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ед.</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Кол-во</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Средняя с/с</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Сумма</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((it) => (
                <tr key={it.id}>
                  <td className="px-4 py-2 text-sm text-gray-900 text-left">{it.name}</td>
                  <td className="px-4 py-2 text-sm text-gray-600 text-left">{it.unit}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{formatInteger(Number(it.quantity))}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{it.averageCost != null ? formatNumber(Number(it.averageCost)) : '-'}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{it.totalCost != null ? formatNumber(Number(it.totalCost)) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default ReportsInventoryBalances


